/**
 * Figursky product page runtime.
 *
 * Replaces the reactive parts of app/pages/products/[slug].vue:
 *   - the variant picker (headless-ui listbox -> native <select> per option)
 *   - price / compare-at price / Afterpay / availability / `?variant=` URL
 *   - the gallery focus for the selected variant's featured media
 *   - the details accordion (vue-collapsed)
 *   - gift-card line-item properties and their validation
 *   - the Send a Hint form (Nuxt /api/send-a-hint -> settings.hint_api_url)
 *
 * Depends on window.figursky from assets/theme.js (drawers + Ajax cart).
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-product]');
  if (!root) return;

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function all(selector, scope) {
    return Array.prototype.slice.call((scope || root).querySelectorAll(selector));
  }

  function one(selector, scope) {
    return (scope || root).querySelector(selector);
  }

  function toggleHidden(element, hidden) {
    if (element) element.hidden = !!hidden;
  }

  function parseJsonScript(selector) {
    var node = one(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[figursky] product json', selector, error);
      return null;
    }
  }

  var variants = parseJsonScript('[data-product-variants]') || [];
  var display = parseJsonScript('[data-variant-display]') || {};
  var productTitle = (one('[data-product-title]') || {}).textContent || '';
  productTitle = productTitle.trim();

  var selects = all('[data-variant-option]').sort(function (a, b) {
    return Number(a.getAttribute('data-variant-option')) - Number(b.getAttribute('data-variant-option'));
  });

  /* ------------------------------------------------------------- variants */

  function findVariant() {
    if (!variants.length) return null;
    if (!selects.length) return variants[0];

    var chosen = selects.map(function (select) {
      return select.value;
    });

    for (var i = 0; i < variants.length; i++) {
      var options = variants[i].options || [];
      var match = true;
      for (var j = 0; j < chosen.length; j++) {
        if (options[j] !== chosen[j]) {
          match = false;
          break;
        }
      }
      if (match) return variants[i];
    }
    return null;
  }

  var currentVariant = findVariant();

  function variantSubtitle(variant) {
    if (!variant || !variant.title || variant.title === 'Default Title') return '';
    return variant.title;
  }

  function focusGalleryMedia(mediaId) {
    var slides = all('[data-media-id]');
    if (!slides.length || !mediaId) return;

    var target = null;
    slides.forEach(function (slide) {
      if (slide.getAttribute('data-media-id') === String(mediaId)) target = slide;
    });

    if (target && target.scrollIntoView) {
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  }

  function updateUrl(variant) {
    if (!variant || !window.history || !window.history.replaceState) return;
    var url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  function applyVariant(variant, isInitial) {
    currentVariant = variant;

    // No variant matches the chosen combination: keep the copy, but make the
    // piece unbuyable, exactly as the Vue page did with an undefined variant.
    if (!variant) {
      var missingButton = one('[data-add-to-cart]');
      if (missingButton) {
        missingButton.disabled = true;
        toggleHidden(one('[data-atc-default]', missingButton), true);
        toggleHidden(one('[data-atc-soldout]', missingButton), false);
      }
      return;
    }

    var info = display[String(variant.id)] || {};
    var shouldEnquire = variant.price === 0;
    var subtitle = variantSubtitle(variant);

    all('[data-variant-price]').forEach(function (element) {
      element.textContent = info.price || '';
    });
    all('[data-compare-price]').forEach(function (element) {
      element.textContent = info.compare_at_price || '';
      toggleHidden(element, !info.has_compare);
    });
    all('[data-afterpay]').forEach(function (element) {
      toggleHidden(element, !info.afterpay_eligible);
    });
    all('[data-afterpay-price]').forEach(function (element) {
      element.textContent = info.afterpay || '';
    });
    all('[data-price-block]').forEach(function (element) {
      toggleHidden(element, shouldEnquire);
    });
    all('[data-variant-title]').forEach(function (element) {
      element.textContent = subtitle;
      toggleHidden(element, subtitle === '');
    });

    var enquireProduct = one('[data-enquire-product]');
    if (enquireProduct) {
      enquireProduct.value = subtitle ? productTitle + ' — ' + subtitle : productTitle;
    }

    var enquireButton = one('[data-enquire-button]');
    toggleHidden(enquireButton, !shouldEnquire);

    var addButton = one('[data-add-to-cart]');
    if (addButton) {
      toggleHidden(addButton, shouldEnquire);
      addButton.disabled = !variant.available;
      toggleHidden(one('[data-atc-default]', addButton), !variant.available);
      toggleHidden(one('[data-atc-soldout]', addButton), variant.available);
      var atcPrice = one('[data-atc-price]', addButton);
      if (atcPrice) atcPrice.textContent = info.price || '';
    }

    if (!isInitial) {
      clearAddError();
      updateUrl(variant);
      focusGalleryMedia(info.media_id);
    }
  }

  selects.forEach(function (select) {
    select.addEventListener('change', function () {
      applyVariant(findVariant(), false);
    });
  });

  applyVariant(currentVariant, true);

  /* ------------------------------------------------------------ accordion */

  function collapseAccordion() {
    all('[data-accordion-item]').forEach(function (item) {
      item.classList.remove('is-expanded');
      var toggle = one('[data-accordion-toggle]', item);
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      // `inert` rather than `hidden` so the grid-rows collapse still animates
      // while the panel's links and buttons leave the tab order.
      var panel = one('[data-accordion-panel]', item);
      if (panel) panel.setAttribute('inert', '');
    });
  }

  root.addEventListener('click', function (event) {
    if (!event.target.closest) return;

    // DetailsAccordion.openShipping() collapsed the open item before opening
    // the drawer; the drawer itself is opened by assets/theme.js.
    if (event.target.closest('[data-details-accordion] [data-drawer-open]')) {
      collapseAccordion();
      return;
    }

    var toggle = event.target.closest('[data-accordion-toggle]');
    if (!toggle) return;

    event.preventDefault();
    var item = toggle.closest('[data-accordion-item]');
    if (!item) return;

    var wasExpanded = item.classList.contains('is-expanded');
    collapseAccordion();

    if (wasExpanded) return;
    item.classList.add('is-expanded');
    toggle.setAttribute('aria-expanded', 'true');
    var panel = one('[data-accordion-panel]', item);
    if (panel) panel.removeAttribute('inert');
  });

  /* ----------------------------------------------------------- gift cards */

  var giftCardFields = one('[data-gift-card-fields]');

  function giftCardInputs() {
    return giftCardFields ? all('[data-gift-card-input]', giftCardFields) : [];
  }

  function validateGiftCard() {
    var valid = true;
    giftCardInputs().forEach(function (input) {
      input.value = input.value.trim();
      var invalid = false;
      if (input.hasAttribute('data-required') && input.value === '') invalid = true;
      if (input.hasAttribute('data-email') && !EMAIL_RE.test(input.value)) invalid = true;
      input.classList.toggle('has-error', invalid);
      if (invalid) valid = false;
    });
    return valid;
  }

  function giftCardProperties() {
    var properties = {};
    giftCardInputs().forEach(function (input) {
      var name = input.getAttribute('name') || '';
      var key = name.replace('properties[', '').replace(']', '');
      if (key) properties[key] = input.value;
    });
    return properties;
  }

  function resetGiftCard() {
    giftCardInputs().forEach(function (input) {
      input.value = '';
      input.classList.remove('has-error');
    });
  }

  if (giftCardFields) {
    giftCardFields.addEventListener('input', function (event) {
      if (event.target.classList) event.target.classList.remove('has-error');
    });
  }

  /* ---------------------------------------------------------- add to cart */

  var addToCartButton = one('[data-add-to-cart]');
  var isAdding = false;

  function setAdding(state) {
    isAdding = state;
    if (!addToCartButton) return;
    var available = !!(currentVariant && currentVariant.available);
    addToCartButton.classList.toggle('is-loading', state);
    toggleHidden(one('[data-atc-loading]', addToCartButton), !state);
    toggleHidden(one('[data-atc-default]', addToCartButton), state || !available);
    toggleHidden(one('[data-atc-soldout]', addToCartButton), state || available);
  }

  function clearAddError() {
    var node = one('[data-add-error]');
    if (!node) return;
    node.textContent = '';
    toggleHidden(node, true);
  }

  function showAddError(message) {
    var node = one('[data-add-error]');
    if (!node) return;
    node.textContent = message || 'Could not add to cart. Please reload the page and try again.';
    toggleHidden(node, false);
  }

  if (addToCartButton) {
    addToCartButton.addEventListener('click', function (event) {
      event.preventDefault();
      if (isAdding || !currentVariant || !window.figursky) return;
      if (giftCardFields && !validateGiftCard()) return;

      var properties = giftCardFields ? giftCardProperties() : null;
      clearAddError();
      setAdding(true);

      window.figursky.cart
        .add(currentVariant.id, 1, properties)
        .then(function () {
          if (giftCardFields) resetGiftCard();
        })
        .catch(function (error) {
          // window.figursky.cart already logged it; the shopper needs to see it
          // too — /cart/add.js rejects with the "sold out" description when the
          // variant ran out between render and click.
          showAddError(error && error.message);
        })
        .then(function () {
          setAdding(false);
        });
    });
  }

  /* -------------------------------------------------------- send a hint */

  var hintForm = one('[data-hint-form]');

  function resetHint() {
    if (!hintForm) return;
    hintForm.reset();
    all('[data-field]', hintForm).forEach(function (field) {
      field.classList.remove('has-error');
      toggleHidden(one('[data-field-error]', field), true);
    });
    toggleHidden(one('[data-hint-error]'), true);
    toggleHidden(one('[data-hint-sent]'), true);
    toggleHidden(one('[data-hint-form-wrap]'), false);
    if (window.turnstile && window.turnstile.reset) window.turnstile.reset();
  }

  function validateHint() {
    var valid = true;
    all('[data-hint-input]', hintForm).forEach(function (input) {
      var field = input.closest('[data-field]');
      var invalid = false;

      if (input.name === 'name' && input.value.trim() === '') invalid = true;
      if (input.hasAttribute('data-email') && !EMAIL_RE.test(input.value.trim())) invalid = true;

      if (field) {
        field.classList.toggle('has-error', invalid);
        toggleHidden(one('[data-field-error]', field), !invalid);
      }
      if (invalid) valid = false;
    });
    return valid;
  }

  function turnstileToken() {
    if (window.turnstile && window.turnstile.getResponse) {
      try {
        return window.turnstile.getResponse() || '';
      } catch (error) {
        return '';
      }
    }
    var field = hintForm.querySelector('[name="cf-turnstile-response"]');
    return field ? field.value : '';
  }

  if (hintForm) {
    var hintSubmit = one('[data-hint-submit]');
    var hintSending = false;

    hintForm.addEventListener('input', function (event) {
      var field = event.target.closest ? event.target.closest('[data-field]') : null;
      if (!field) return;
      field.classList.remove('has-error');
      toggleHidden(one('[data-field-error]', field), true);
    });

    hintForm.addEventListener('submit', function (event) {
      event.preventDefault();
      if (hintSending) return;

      var endpoint = hintForm.getAttribute('data-hint-endpoint');
      if (!endpoint) return;
      if (!validateHint()) return;

      var errorNode = one('[data-hint-error]');
      toggleHidden(errorNode, true);
      hintSending = true;
      if (hintSubmit) hintSubmit.classList.add('is-loading');

      var payload = {
        name: (one('[name="name"]', hintForm) || {}).value || '',
        email: (one('[name="email"]', hintForm) || {}).value || '',
        to: (one('[name="to"]', hintForm) || {}).value || '',
        message: (one('[name="message"]', hintForm) || {}).value || '',
        product: root.getAttribute('data-product-handle'),
        variant: currentVariant ? currentVariant.id : null,
        turnstile: turnstileToken(),
      };

      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (result) {
          if (result && result.success) {
            toggleHidden(one('[data-hint-form-wrap]'), true);
            toggleHidden(one('[data-hint-sent]'), false);
            return;
          }
          throw new Error((result && result.message) || '');
        })
        .catch(function (error) {
          if (errorNode) {
            errorNode.textContent =
              error.message || 'Something went wrong. Please reload the page and try again.';
            toggleHidden(errorNode, false);
          }
          if (window.turnstile && window.turnstile.reset) window.turnstile.reset();
        })
        .then(function () {
          hintSending = false;
          if (hintSubmit) hintSubmit.classList.remove('is-loading');
        });
    });

    document.addEventListener('click', function (event) {
      var opener = event.target.closest ? event.target.closest('[data-drawer-open="hint"]') : null;
      if (opener) resetHint();
    });
  }

  /* ------------------------------------------------- contact form return */

  // Shopify reloads the page after a {% form 'contact' %} submit; re-open the
  // drawer so the success message (or the errors) are visible.
  var autoOpen = one('[data-drawer-autoopen]');
  if (autoOpen && window.figursky) {
    window.figursky.openDrawer(autoOpen.getAttribute('data-drawer-autoopen'));
  }
})();
